/* eslint-disable react-hooks/exhaustive-deps */
import { ClearableSearchInput } from "components/SearchField/SearchField";
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";
import {
  LuBell,
  LuCheck,
  LuCheckCheck,
  LuChevronDown,
  LuChevronLeft,
  LuChevronRight,
  LuChevronsUpDown,
  LuCoins,
  LuCopy,
  LuDownload,
  LuEllipsis,
  LuEye,
  LuFileText,
  LuMail,
  LuMenu,
  LuPencil,
  LuPlus,
  LuSearch,
  LuSend,
  LuTrash2,
  LuX,
} from "react-icons/lu";
import { useMaterialUIController } from "context";
import CatalogueItemModal from "components/CatalogueItemModal";
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import LoadingModal from "components/LoadingModel/loadingModal";
import ListingActionMenu from "components/ListingActionMenu";
import { CardSkeleton, Skeleton, TableSkeleton } from "components/Skeleton";
import { appConstants } from "utils/appConstants";
import { clearQuotationNumberPreview, getQuotationNumberPreview } from "utils/quotationNumberPreview";
import { getScreenRight, showResMsg } from "utils/generic";
import { genericMessage } from "utils/genericMessages";
import { formatCurrencyAUD } from "utils/formatCurrency";
import { quotationDateFilter } from "utils/quotationDateFilter";
import { documentDateFilterLabel } from "utils/documentDateFilterLabel";
import {
  parseAdditionalQuotationEmails,
  validateAdditionalQuotationEmails,
  validateQuotationCustomerField,
} from "utils/quotationCustomerValidation";
import {
  downloadQuotation,
  prepareQuotationEmail,
  prepareQuotationEmailDocument,
} from "utils/quotationAPI";
import { captureWebsiteDocument, savePdfResponse } from "utils/documentPdf";
import {
  DashboardNavigation,
  ProfileMenu,
} from "layouts/businessUserDashboard";
import AppHeader from "components/AppHeader";
import { SocketContext } from "socket/SocketContext";
import QuotationRecipientField from "./QuotationRecipientField";
import DocumentSentSuccessDialog from "./DocumentSentSuccessDialog";
import { showDraftSavedMessage } from "components/InlineFeedback/DraftSaveConfirmation";
import "./quotation.css";

const PAGE_SIZE = 10;

const readCachedTabCounts = (key) => {
  try {
    const counts = key && JSON.parse(sessionStorage.getItem(key));
    if (
      !counts ||
      !Number.isInteger(counts.All) ||
      !Object.values(counts).every(
        (count) => Number.isInteger(count) && count >= 0
      )
    )
      return null;
    return { counts, amounts: {} };
  } catch {
    return null;
  }
};

const cacheTabCounts = (key, counts) => {
  if (!key) return;
  try {
    if (counts) sessionStorage.setItem(key, JSON.stringify(counts));
    else sessionStorage.removeItem(key);
  } catch {
    // Counts still load normally when browser storage is unavailable.
  }
};

const numberValue = (value) =>
  Number(String(value ?? 0).replace(/[^0-9.-]/g, "")) || 0;

const getSavedRecipientEmail = (quote) =>
  String(
    quote?.recipientEmail ||
      quote?.customerEmailAddress ||
      quote?.customerEmail ||
      quote?.email ||
      quote?.contactEmail ||
      ""
  ).trim();

const formatStatAmount = (val) => "$" + formatCurrencyAUD(numberValue(val));

const formatCreatedDate = (row) => {
  const val = row.quotationDate || row.createdAt;
  if (!val) return "—";
  const parsed = new Date(val);
  return Number.isNaN(parsed.getTime())
    ? String(val)
    : parsed.toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};

const getQuotationValidUntil = (row) => {
  const val =
    row.validUptoDate ||
    row.validUntil ||
    row.expiryDate ||
    row.quotationExpiryDate;
  if (val) {
    const savedDate = new Date(val);
    if (!Number.isNaN(savedDate.getTime())) return savedDate;
  }

  // Older list responses omit the expiry date but include the saved validity term.
  const baseDate = row.quotationDate || row.createdDate || row.createdAt;
  if (!baseDate) return null;
  const date = new Date(
    /^\d{4}-\d{2}-\d{2}$/.test(baseDate) ? `${baseDate}T12:00:00` : baseDate
  );
  if (Number.isNaN(date.getTime())) return null;
  const term = String(row.validUpto || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
  const days = term.match(/^(7|14|30) days?(?: (end of month))?$/);
  if (term !== "same day" && !days) return null;

  date.setHours(12, 0, 0, 0);
  if (days?.[2]) date.setMonth(date.getMonth() + 1, 0);
  date.setDate(date.getDate() + (days ? Number(days[1]) : 0));
  return date;
};

const formatValidUntilDesktop = (row) => {
  const parsed = getQuotationValidUntil(row);
  if (!parsed) return "—";
  return parsed.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatValidUntilTabletMobile = (row) => {
  const parsed = getQuotationValidUntil(row);
  if (!parsed) return "No active expiry";
  return `Valid until ${parsed.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })}`;
};

const rowStatus = (row) => {
  const status = String(
    row.activityStatus || row.quotationStatus || "Draft"
  ).trim();
  return /pending/i.test(status)
    ? row.emailSent > 0
      ? "Sent"
      : "Draft"
    : status;
};

const isFollowUpQuote = (row) =>
  Number(row.emailSent || 0) > 0 ||
  ["sent", "viewed", "awaiting decision"].includes(
    rowStatus(row).toLowerCase()
  );

const getQuoteOpenPath = (row) =>
  rowStatus(row).toLowerCase() === "draft"
    ? `/quotations/edit/${row._id}?type=edit`
    : `/quotations/view/${row._id}`;

const getQuoteOpenState = (row) => ({
  quotationSummary: {
    _id: row._id,
    quotationNumber: row.quotationNumber,
    quotationStatus: row.quotationStatus || rowStatus(row),
    invoiceID: row.invoiceID || "",
    contactID: row.contactID,
    contactName: row.customerCompanyName || row.customerName || row.name || "",
    recipientEmail: row.recipientEmail || row.customerEmailAddress || "",
    finalAmount: row.finalAmount,
  },
});

const getContactOpenPath = (row) => {
  const contact = row?.contactID ?? row?.customerID ?? row?.contact;
  const contactId = typeof contact === "object" ? contact?._id : contact;
  return contactId ? `/contacts/${contactId}` : null;
};

const statusKey = (row) => rowStatus(row).toLowerCase().replace(/\s+/g, "-");

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const SelectDropdown = memo(
  ({ value, onChange, options, ariaLabel, className = "" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = React.useRef(null);

    useEffect(() => {
      const handleOutside = (e) => {
        if (containerRef.current && !containerRef.current.contains(e.target)) {
          setIsOpen(false);
        }
      };
      if (isOpen) {
        document.addEventListener("mousedown", handleOutside);
      }
      return () => document.removeEventListener("mousedown", handleOutside);
    }, [isOpen]);

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    const selectedLabel = useMemo(() => {
      const found = options.find((opt) =>
        typeof opt === "string" ? opt === value : opt.value === value
      );
      if (!found) return value;
      return typeof found === "string" ? found : found.label;
    }, [options, value]);

    return (
      <div className={`quotation-select-wrap ${className}`} ref={containerRef}>
        <button
          type="button"
          className={`quotation-select-trigger${isOpen ? " is-open" : ""}`}
          onClick={() => setIsOpen((prev) => !prev)}
          onKeyDown={handleKeyDown}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={ariaLabel || selectedLabel}
        >
          <span className="quotation-select-value">{selectedLabel}</span>
          <LuChevronDown className="select-arrow" />
        </button>

        {isOpen && (
          <div className="quotation-dropdown-panel" role="listbox">
            {options.map((opt) => {
              const optVal = typeof opt === "string" ? opt : opt.value;
              const optLabel = typeof opt === "string" ? opt : opt.label;
              const isSelected = optVal === value;
              return (
                <button
                  key={optVal}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`quotation-dropdown-item${
                    isSelected ? " is-selected" : ""
                  }`}
                  onClick={() => {
                    onChange(optVal);
                    setIsOpen(false);
                  }}
                >
                  <span className="quotation-dropdown-item-label">
                    {optLabel}
                  </span>
                  {isSelected && (
                    <LuCheck className="quotation-dropdown-check-icon" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }
);

function Quotations() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routeTab =
    searchParams.get("activityStatus") === "Awaiting decision"
      ? "Awaiting decision"
      : "All";
  const [controller, controllerDispatch] = useMaterialUIController();
  const darkMode = Boolean(controller.darkMode);
  const loginDetails = useMemo(
    () => JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "{}"),
    []
  );
  const tabCountsCacheKey = loginDetails.businessUserID
    ? `QUOTATION_TAB_COUNTS:${loginDetails.businessUserID}`
    : null;

  useEffect(() => {
    if (loginDetails.businessUserID)
      getQuotationNumberPreview(loginDetails.businessUserID).catch(() => {});
  }, [loginDetails.businessUserID]);

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [statusSummary, setStatusSummary] = useState(() =>
    readCachedTabCounts(tabCountsCacheKey)
  );
  const listRequestRef = useRef(null);
  const quietListRefreshRef = useRef(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const requestedTab = new URLSearchParams(window.location.search).get("tab");
  const quotationTabs = [
    "All",
    "Draft",
    "Awaiting decision",
    "Approved",
    "Declined",
    "Expired",
    "Voided",
  ];
  const [activeTab, setActiveTab] = useState(
    quotationTabs.includes(requestedTab) ? requestedTab : "All"
  );
  useEffect(() => {
    if (routeTab !== "All") {
      setActiveTab(routeTab);
      setPage(1);
    }
  }, [routeTab]);
  const [customer, setCustomer] = useState("All customers");
  const [dateFilter, setDateFilter] = useState("All dates");
  const [screenAccess, setScreenAccess] = useState(false);
  const [invoiceAccess, setInvoiceAccess] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteNumber, setDeleteNumber] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteInFlightRef = useRef(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendQuoteRow, setSendQuoteRow] = useState(null);
  const [sendRecipient, setSendRecipient] = useState("");
  const [sendRecipientLoading, setSendRecipientLoading] = useState(false);
  const [additionalRecipients, setAdditionalRecipients] = useState("");
  const [additionalRecipientsError, setAdditionalRecipientsError] =
    useState("");
  const [sendSubject, setSendSubject] = useState("");
  const [sendAction, setSendAction] = useState("send");
  const [sendMessage, setSendMessage] = useState("");
  const [sentQuote, setSentQuote] = useState(null);
  const [attachPdf, setAttachPdf] = useState(true);
  const [sendCopy, setSendCopy] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendRecipientError, setSendRecipientError] = useState("");
  const [sendError, setSendError] = useState("");
  const [approvalQuote, setApprovalQuote] = useState(null);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalNotifications, setApprovalNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(
    () =>
      new URLSearchParams(window.location.search).get("notifications") ===
      "true"
  );
  const socket = useContext(SocketContext);

  const { organisation, role, userName, profileImage, planName } =
    useMemo(() => {
      const organisation =
        loginDetails.companyName ||
        loginDetails.businessName ||
        loginDetails.organisationName ||
        loginDetails.organizationName ||
        loginDetails.fullName ||
        loginDetails.name ||
        `${loginDetails.firstName || ""} ${
          loginDetails.lastName || ""
        }`.trim() ||
        "Invoice & Recover";
      const role = loginDetails.roleName || loginDetails.userType || "Owner";
      const userName =
        loginDetails.fullName ||
        `${loginDetails.firstName || ""} ${
          loginDetails.lastName || ""
        }`.trim() ||
        loginDetails.name ||
        organisation ||
        loginDetails.emailAddress ||
        loginDetails.email;
      const profileFileName =
        loginDetails.profilePictureFileName || loginDetails.profilePicFileName;
      const profileImage = profileFileName
        ? `${appConstants.baseURL}user/viewFile?fileName=${encodeURIComponent(
            profileFileName
          )}`
        : "";
      const planName =
        loginDetails.planName || loginDetails.subscriptionPlan || "SILVER";
      return { organisation, role, userName, profileImage, planName };
    }, [loginDetails]);

  const values = useMemo(
    () => ({
      businessUserID: loginDetails.businessUserID,
      sortingType: "",
      sortingField: "",
      quotationStatus: "All",
      activityStatus: activeTab,
      customer,
      ...quotationDateFilter(dateFilter),
      page,
      limit: PAGE_SIZE,
      filterValues: search,
      searchParameterList: [
        "quotationNumber",
        "activityStatus",
        "quotationStatus",
        "quotationDate",
        "customerName",
        "finalAmount",
        "customerCompanyName",
      ],
      token: loginDetails.token,
    }),
    [activeTab, customer, dateFilter, loginDetails, page, search]
  );

  const fetchQuotations = useCallback(
    async ({ quiet = false } = {}) => {
      listRequestRef.current?.abort();
      const request = new AbortController();
      listRequestRef.current = request;
      const showSkeleton = !quiet && !quietListRefreshRef.current;
      quietListRefreshRef.current = false;
      if (showSkeleton) setLoading(true);
      try {
        const response = await axios.post(
          `${appConstants.baseURL}quotation/fetchQuotations`,
          values,
          { signal: request.signal }
        );
        if (listRequestRef.current !== request) return;
        if (response.data?.Result_Status === false) {
          throw new Error(
            response.data.Result_Message || "Quotation filtering failed."
          );
        }
        const output = response.data?.Result_Output || {};
        setRows(output.docs || []);
        setCustomers(output.customerOptions || []);
        setTotalRows(output.totalDocs || 0);
        if (output.statusCounts) {
          setStatusSummary({
            counts: output.statusCounts,
            amounts: output.statusAmounts || {},
            approvedToInvoice: output.approvedToInvoice,
          });
          if (
            values.customer === "All customers" &&
            !values.durationDate &&
            !values.filterValues
          ) {
            cacheTabCounts(tabCountsCacheKey, output.statusCounts);
          }
        }
      } catch (error) {
        if (!axios.isCancel(error) && listRequestRef.current === request) {
          setRows([]);
          setTotalRows(0);
          setStatusSummary(null);
          cacheTabCounts(tabCountsCacheKey, null);
          showResMsg(
            "Unable to load quotations for these filters. Please try again.",
            2000,
            false
          );
          console.error("Error fetching quotations", error);
        }
      } finally {
        if (listRequestRef.current === request) setLoading(false);
      }
    },
    [values, tabCountsCacheKey]
  );

  useEffect(() => {
    if (!socket || !loginDetails.businessUserID) return undefined;
    const fetchNotifications = () =>
      socket.emit("message", { businessUserID: loginDetails.businessUserID });
    const receiveNotifications = (notifications) =>
      setApprovalNotifications(
        Array.isArray(notifications)
          ? notifications.filter(
              (notification) => notification.type === "quotation"
            )
          : []
      );
    const receiveApproval = (event) => {
      if (String(event.businessUserID) !== String(loginDetails.businessUserID))
        return;
      if (event.notification) {
        setApprovalNotifications((previous) => [
          event.notification,
          ...previous.filter(
            (notification) => notification._id !== event.notification._id
          ),
        ]);
      } else fetchNotifications();
      showResMsg(
        event.message || "Quotation approved by customer.",
        3000,
        true
      );
      fetchQuotations();
    };
    const reconnect = () => {
      fetchNotifications();
      fetchQuotations();
    };
    socket.on("initialNotifications", receiveNotifications);
    socket.on("quotationApproved", receiveApproval);
    socket.on("connect", reconnect);
    fetchNotifications();
    return () => {
      socket.off("initialNotifications", receiveNotifications);
      socket.off("quotationApproved", receiveApproval);
      socket.off("connect", reconnect);
    };
  }, [socket, loginDetails.businessUserID, fetchQuotations]);

  useEffect(() => {
    let isMounted = true;

    Promise.all([
      getScreenRight("Quotations"),
      getScreenRight("Invoices"),
    ]).then(([quotationRight, invoiceRight]) => {
      if (!isMounted) return;
      setScreenAccess(quotationRight === "Full Access");
      setInvoiceAccess(invoiceRight === "Full Access");
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(fetchQuotations, search ? 250 : 0);
    return () => {
      clearTimeout(timer);
      listRequestRef.current?.abort();
      listRequestRef.current = null;
    };
  }, [fetchQuotations]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !e.target.closest(".dashboard-profile-control") &&
        !e.target.closest(".dashboard-profile-menu")
      ) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const visibleRows = useMemo(
    () =>
      rows.filter((row) => {
        const customerMatch =
          customer === "All customers" ||
          (row.customerCompanyName || row.customerName) === customer;
        const status = rowStatus(row);
        const tabMatch =
          activeTab === "All" ||
          (activeTab === "Awaiting decision"
            ? ["Sent", "Viewed", "Awaiting decision"].includes(status)
            : status === activeTab);
        return customerMatch && tabMatch;
      }),
    [activeTab, customer, rows]
  );

  const customerOptions = useMemo(
    () => ["All customers", ...customers],
    [customers]
  );

  const dateOptions = useMemo(
    () => [
      { value: "All dates", label: "All dates" },
      { value: "7 days", label: "Last 7 days" },
      { value: "30 days", label: "Last 30 days" },
      { value: "Quarter", label: "This quarter" },
    ],
    []
  );

  const counts = statusSummary?.counts || {};

  const sum = (names) =>
    names.reduce(
      (total, name) =>
        total + Math.round(numberValue(statusSummary?.amounts[name]) * 100),
      0
    ) / 100;

  const countFor = (tab) => {
    if (!statusSummary) return "—";
    if (tab === "All") return counts.All || 0;
    if (tab === "Awaiting decision")
      return (
        (counts.Sent || 0) +
        (counts.Viewed || 0) +
        (counts["Awaiting decision"] || 0)
      );
    return counts[tab] || 0;
  };

  const tabCount = countFor(activeTab);
  const expectedRowCount =
    typeof tabCount === "number" ? tabCount : totalRows || PAGE_SIZE;
  const skeletonRows = Math.min(
    PAGE_SIZE,
    Math.max(1, expectedRowCount - (page - 1) * PAGE_SIZE)
  );

  const tabs = [
    "All",
    "Draft",
    "Awaiting decision",
    "Approved",
    "Declined",
    "Expired",
    "Voided",
  ];

  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const startRow = totalRows ? (page - 1) * PAGE_SIZE + 1 : 0;
  const endRow = Math.min(page * PAGE_SIZE, totalRows);

  const addQuotation = () =>
    screenAccess
      ? navigate("/quotations/add?type=new")
      : showResMsg(genericMessage.notPermission, 2000, false);

  const openQuoteDetails = (row, event) => {
    if (
      event?.target?.closest(
        "a, button, input, select, textarea, [data-row-action]"
      )
    ) {
      return;
    }
    navigate(getQuoteOpenPath(row), { state: getQuoteOpenState(row) });
  };

  const handleQuoteRowKeyDown = (row, event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openQuoteDetails(row, event);
    }
  };

  const requestDelete = (quotation) => {
    if (deleteInFlightRef.current) return;
    if (!screenAccess)
      return showResMsg(genericMessage.notPermission, 2000, false);
    if (isFollowUpQuote(quotation))
      return showResMsg("Sent quotations cannot be deleted.", 2000, false);
    setDeleteId(quotation._id);
    setDeleteNumber(quotation.quotationNumber || "Quotation");
    setDeleteError("");
    setDeleteDialog(true);
    setOpenMenu(null);
  };

  const deleteQuotation = async () => {
    if (!screenAccess || !deleteId || deleteInFlightRef.current) return;
    deleteInFlightRef.current = true;
    setIsDeleting(true);
    setDeleteError("");
    try {
      const response = await axios.post(
        `${appConstants.baseURL}quotation/deleteQuotation`,
        { businessUserID: loginDetails.businessUserID, _id: deleteId },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
          },
          timeout: 30000,
        }
      );
      if (!response.data?.Result_Status)
        throw new Error(
          response.data?.Result_Message ||
            "The quotation could not be deleted. Please try again."
        );
      setDeleteDialog(false);
      const deletedRow = rows.find((row) => row._id === deleteId);
      const successMessage = rowStatus(deletedRow || {}) === "Draft" ? "Draft deleted successfully" : "Quotation deleted successfully";
      if (rows.length === 1 && page > 1) {
        quietListRefreshRef.current = true;
        setPage((previous) => previous - 1);
      } else await fetchQuotations({ quiet: true });
      setDeleteId(null);
      showDraftSavedMessage(successMessage);
    } catch (error) {
      setDeleteError(
        error.response?.data?.Result_Message ||
          (error.code === "ECONNABORTED"
            ? "Deletion is taking longer than expected. Refresh the quotation list before trying again."
            : error.message ||
              "The quotation could not be deleted. Please try again.")
      );
    } finally {
      deleteInFlightRef.current = false;
      setIsDeleting(false);
    }
  };

  const cloneQuotation = async (row) => {
    if (actionLoading) return;
    if (!screenAccess)
      return showResMsg(genericMessage.notPermission, 2000, false);
    setOpenMenu(null);
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${appConstants.baseURL}quotation/cloneQuotation`,
        {
          businessUserID: loginDetails.businessUserID,
          quotationID: row._id,
          userID: loginDetails._id,
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
          },
          timeout: 30000,
        }
      );
      const data = response.data;
      if (data?.Result_Output === "NO AVAILABLE CREDITS") {
        navigate("/subscriptions");
      } else if (data?.Result_Status && data.Result_Output?._id) {
        clearQuotationNumberPreview(loginDetails.businessUserID);
        navigate(`/quotations/edit/${data.Result_Output._id}`);
      } else {
        showResMsg(
          data?.Result_Message || "Unable to duplicate the quotation.",
          2500,
          false
        );
      }
    } catch (error) {
      console.error("Error cloning quotation", error);
      showResMsg(
        error.response?.data?.Result_Message ||
          error.response?.data?.message ||
          "Unable to duplicate the quotation. Check the quotations list before trying again.",
        3000,
        false
      );
    } finally {
      setActionLoading(false);
    }
  };

  const createInvoice = (row) => {
    if (!screenAccess || !invoiceAccess) {
      showResMsg(genericMessage.notPermission, 2000, false);
      return;
    }
    setOpenMenu(null);
    navigate(`/invoices/add?quotationID=${row._id}`, {
      state: {
        quotationID: row._id,
        quotationNumber: row.quotationNumber,
        quotation: row,
      },
    });
  };

  const awaitingDecisionCount =
    (counts.Sent || 0) +
    (counts.Viewed || 0) +
    (counts["Awaiting decision"] || 0);

  const showClearFilters = Boolean(search.trim()) || customer !== "All customers" || dateFilter !== "All dates";
  const hasActiveFilters = Boolean(
    search.trim() !== "" ||
      customer !== "All customers" ||
      dateFilter !== "All dates" ||
      activeTab !== "All"
  );

  const clearFilters = () => {
    setSearch("");
    setCustomer("All customers");
    setDateFilter("All dates");
    setActiveTab("All");
    setPage(1);
  };

  const handleOpenSendModal = (row, event) => {
    event?.stopPropagation();
    setOpenMenu(null);
    setSendQuoteRow(row);
    const custEmail = getSavedRecipientEmail(row);
    const qNumber = row.quotationNumber || "—";
    const amount = formatCurrencyAUD(numberValue(row.finalAmount));
    const isFollowUp = isFollowUpQuote(row);
    setSendAction(isFollowUp ? "followUp" : "send");
    setSendMessage(
      isFollowUp
        ? `Just following up on quote ${qNumber} for $${amount} AUD from ${organisation}. You can review the details and approve your quote online.`
        : ""
    );
    setSendRecipient(custEmail);
    setSendRecipientLoading(!custEmail);
    setAdditionalRecipients("");
    setAdditionalRecipientsError("");
    setSendRecipientError("");
    setSendError("");
    setSendSubject(`Quote ${qNumber} from ${organisation} · $${amount} AUD`);
    setAttachPdf(true);
    setSendCopy(false);
    setSendModalOpen(true);
  };

  useEffect(() => {
    if (
      !sendModalOpen ||
      !sendQuoteRow?._id ||
      getSavedRecipientEmail(sendQuoteRow)
    )
      return undefined;
    let active = true;
    const request = new AbortController();
    setSendRecipientLoading(true);
    axios
      .post(
        `${appConstants.baseURL}quotation/fetchQuotationDetailsByID`,
        {
          quotationID: sendQuoteRow._id,
          businessUserID: loginDetails.businessUserID,
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
          },
          signal: request.signal,
          timeout: 30000,
        }
      )
      .then(({ data }) => {
        if (!active) return;
        if (!data?.Result_Status)
          throw new Error(
            data?.Result_Message || "Unable to load the quotation email."
          );
        const email = getSavedRecipientEmail(data.Result_Output?.[0]);
        setSendRecipient(email);
        setSendRecipientError(
          email
            ? ""
            : "No recipient email was found. Add it in the quotation editor before sending."
        );
      })
      .catch((error) => {
        if (active)
          setSendRecipientError(
            error.response?.data?.Result_Message ||
              error.message ||
              "Unable to load the quotation email. Reopen Send to try again."
          );
      })
      .finally(() => {
        if (active) setSendRecipientLoading(false);
      });
    return () => {
      active = false;
      request.abort();
    };
  }, [sendModalOpen, sendQuoteRow, loginDetails.businessUserID]);

  useEffect(() => {
    if (sendModalOpen) prepareQuotationEmail();
  }, [sendModalOpen]);

  const sendDocumentRef = useRef(null);
  useEffect(() => {
    if (!sendModalOpen || !sendQuoteRow?._id) return undefined;
    const prepared = {
      id: String(sendQuoteRow._id),
      snapshot: captureWebsiteDocument("quotation", sendQuoteRow._id).catch(
        () => null
      ),
    };
    sendDocumentRef.current = prepared;
    return () => {
      if (sendDocumentRef.current === prepared) sendDocumentRef.current = null;
    };
  }, [sendModalOpen, sendQuoteRow?._id]);

  const handleSendQuoteSubmit = async () => {
    if (!sendQuoteRow || sendingEmail || actionLoading || sendRecipientLoading)
      return;
    const emailError = validateQuotationCustomerField("email", sendRecipient);
    const additionalError =
      validateAdditionalQuotationEmails(additionalRecipients);
    setSendRecipientError(emailError);
    setAdditionalRecipientsError(additionalError);
    if (emailError || additionalError) {
      document.getElementById("send-recipient-input")?.focus();
      return;
    }
    if (sendAction === "followUp" && !sendMessage.trim()) {
      setSendError("Please enter a follow-up message.");
      document.getElementById("send-message-input")?.focus();
      return;
    }
    setSendError("");
    setSendingEmail(true);
    try {
      const prepared = sendDocumentRef.current;
      const documentPayload = await prepareQuotationEmailDocument(
        sendQuoteRow._id,
        prepared?.id === String(sendQuoteRow._id)
          ? prepared.snapshot
          : undefined
      );
      const response = await axios.post(
        `${appConstants.baseURL}quotation/emailReminderQuotation`,
        {
          quotationID: sendQuoteRow._id,
          userID: loginDetails._id,
          businessUserID: loginDetails.businessUserID,
          recipientEmail: sendRecipient.trim(),
          additionalRecipientEmails:
            parseAdditionalQuotationEmails(additionalRecipients),
          emailSubject: sendSubject.trim(),
          ...(sendAction === "followUp"
            ? {
                communicationType: "followUp",
                emailMessage: sendMessage.trim(),
              }
            : {}),
          attachPdf,
          sendCopy,
          ...documentPayload,
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
          },
          timeout: 60000,
        }
      );
      if (!response.data?.Result_Status) {
        setSendError(
          response.data?.Result_Message ||
            "Unable to send quotation. Please try again."
        );
        if (response.data?.Result_Output?.partiallySent) fetchQuotations();
        return;
      }
      setSendModalOpen(false);
      setSendQuoteRow(null);
      setSentQuote({
        id: sendQuoteRow._id,
        number: sendQuoteRow.quotationNumber,
        recipient: sendRecipient,
        followUp: sendAction === "followUp",
      });
      fetchQuotations();
    } catch (error) {
      console.error("Error sending quotation", error);
      setSendError(
        error.response?.data?.Result_Message ||
          error.response?.data?.message ||
          "Unable to send quotation. Please try again."
      );
    } finally {
      setSendingEmail(false);
    }
  };

  const loadQuoteForReview = async (row) => {
    const response = await axios.post(
      `${appConstants.baseURL}quotation/fetchQuotationDetailsByID`,
      { quotationID: row._id, businessUserID: loginDetails.businessUserID },
      {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}` },
        timeout: 30000,
      }
    );
    const details = response.data?.Result_Output?.[0];
    if (!response.data?.Result_Status || !details) {
      throw new Error(
        response.data?.Result_Message ||
          "Unable to load the quotation for review."
      );
    }
    return {
      ...details,
      name:
        details.customerCompanyName ||
        details.customerName ||
        details.name ||
        "Customer",
    };
  };

  const handleMarkAsApproved = async (row) => {
    if (actionLoading || approvalLoading) return;
    if (!screenAccess)
      return showResMsg(genericMessage.notPermission, 2000, false);
    setOpenMenu(null);
    setApprovalLoading(true);
    try {
      const details = await loadQuoteForReview(row);
      if (!details.items?.length)
        throw new Error(
          "Add at least one item before approving this quotation."
        );
      const response = await axios.post(
        `${appConstants.baseURL}quotation/updateQuotation`,
        {
          ...details,
          itemsList: details.items,
          quotationID: row._id,
          activityStatus: "Approved",
          quotationStatus: "Approved",
          userID: loginDetails._id,
          businessUserID: loginDetails.businessUserID,
          sendEmailFlag: false,
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
          },
        }
      );
      if (!response.data?.Result_Status) {
        throw new Error(
          response.data?.Result_Message ||
            "Unable to approve the quotation. Please try again."
        );
      }
      await fetchQuotations({ quiet: true });
      setApprovalQuote(null);
      showDraftSavedMessage("Quotation approved successfully");
    } catch (error) {
      console.error("Error marking quote as approved", error);
      const message =
        error.response?.data?.Result_Message ||
        error.message ||
        "Unable to approve the quotation. Please try again.";
      showResMsg(message, 2000, false);
    } finally {
      setApprovalLoading(false);
    }
  };

  const paginationSummaryText = useMemo(() => {
    if (loading) {
      return "Loading records · Counts and summaries will appear when ready";
    }
    if (visibleRows.length === 0) {
      return "Showing 0 quotes · $0 matching quote value";
    }
    return `Showing ${startRow}–${endRow} of ${totalRows} quotes`;
  }, [loading, visibleRows.length, startRow, endRow, totalRows]);

  const renderStateMessage = () => {
    if (loading) {
      return null;
    }
    if (totalRows === 0 && !hasActiveFilters) {
      return (
        <div className="quotation-state-message">
          <div className="quotation-state-icon">
            <LuFileText />
          </div>
          <h4>No quotations yet</h4>
          <p>
            Create a quote for your customer, then send it when you’re ready.
            Track their decision here.
          </p>
          <button
            type="button"
            className="quotation-create-btn"
            onClick={addQuotation}
          >
            <LuPlus />
            <span>Create your first quote</span>
          </button>
        </div>
      );
    }
    return (
      <div className="quotation-state-message">
        <div className="quotation-state-icon">
          <LuFileText />
        </div>
        <h4>No quotes match your filters</h4>
        <p>
          Try a different quote number, customer, or date range. Your quotations
          are still here.
        </p>
        {showClearFilters && (
          <button
            type="button"
            className="quotation-clear-filters-btn"
            onClick={clearFilters}
          >
            Clear filters
          </button>
        )}
      </div>
    );
  };

  const renderActionMenu = (row) => {
    const status = rowStatus(row);
    const isApproved = status.toLowerCase() === "approved";

    return (
      <ListingActionMenu
        anchorEl={menuAnchorEl}
        label={`Actions for ${row.quotationNumber}`}
        onClose={() => {
          setOpenMenu(null);
          setMenuAnchorEl(null);
        }}
      >
        <button
          type="button"
          className="quotation-menu-item"
          disabled={isApproved}
          onClick={() => {
            setOpenMenu(null);
            navigate(`/quotations/edit/${row._id}?type=edit`);
          }}
        >
          <LuPencil className="quotation-menu-icon" />
          <span>Edit</span>
        </button>
        <button
          type="button"
          className="quotation-menu-item"
          onClick={() => {
            setOpenMenu(null);
            navigate(
              `/quotations/quotation/${row.quotationNumber || "quotation"}/${
                row._id
              }`,
              {
                state: {
                  previewReturn: {
                    path: `${window.location.pathname}${
                      window.location.search
                    }`,
                    label: "Back to Quotations",
                  },
                },
              }
            );
          }}
        >
          <LuEye className="quotation-menu-icon" />
          <span>Preview</span>
        </button>
        <button
          type="button"
          className="quotation-menu-item"
          onClick={(event) => handleOpenSendModal(row, event)}
        >
          <LuSend className="quotation-menu-icon" />
          <span>{isFollowUpQuote(row) ? "Follow up" : "Send"}</span>
        </button>
        {!isApproved && (
          <button
            type="button"
            className="quotation-menu-item"
            onClick={() => {
              setOpenMenu(null);
              setApprovalQuote(row);
            }}
          >
            <LuCheckCheck className="quotation-menu-icon" />
            <span>Mark as approved</span>
          </button>
        )}
        {isApproved && !row.invoiceID && (
          <button
            type="button"
            className="quotation-menu-item"
            onClick={() => {
              setOpenMenu(null);
              createInvoice(row);
            }}
          >
            <LuFileText className="quotation-menu-icon" />
            <span>Create invoice</span>
          </button>
        )}
        <button
          type="button"
          className="quotation-menu-item"
          onClick={() => cloneQuotation(row)}
        >
          <LuCopy className="quotation-menu-icon" />
          <span>Duplicate</span>
        </button>
        <button
          type="button"
          className="quotation-menu-item"
          onClick={() => {
            setOpenMenu(null);
            downloadQuotation(
              { _id: row._id },
              (response) => {
                savePdfResponse(response, `quotation_${row.quotationNumber}.pdf`);
              },
              dispatch
            );
          }}
        >
          <LuDownload className="quotation-menu-icon" />
          <span>Download PDF</span>
        </button>
        <button
          type="button"
          className="quotation-menu-item quotation-menu-item-delete"
          disabled={isFollowUpQuote(row) || status.toLowerCase() === "voided"}
          title={
            isFollowUpQuote(row)
              ? "Sent quotations cannot be deleted"
              : status.toLowerCase() === "voided"
              ? "This quotation is already voided"
              : "Move quotation to Voided"
          }
          onClick={() => requestDelete(row)}
        >
          <LuTrash2 className="quotation-menu-icon" />
          <span>Delete</span>
        </button>
      </ListingActionMenu>
    );
  };

  return (
    <div
      className={`quotation-page business-dashboard${darkMode ? " dark" : ""}`}
      data-theme={darkMode ? "dark" : "light"}
    >
      <DashboardNavigation
        open={navOpen}
        onClose={() => setNavOpen(false)}
        organisation={organisation}
        role={role}
        planName={planName}
      />

      <div className="dashboard-main">
        {/* FIXED UNTOUCHABLE APP HEADER (Figma Node 429:22151) */}
        <AppHeader
          onOpenNav={() => setNavOpen(true)}
          userName={userName}
          profileImage={profileImage}
          notificationCount={
            approvalNotifications.filter((notification) => !notification.read)
              .length
          }
          onNotificationClick={() =>
            setNotificationsOpen((previous) => !previous)
          }
        />

        {notificationsOpen && (
          <section
            className="quotation-notifications"
            aria-label="Quotation notifications"
          >
            <header>
              <strong>Quotation notifications</strong>
              <button
                type="button"
                aria-label="Close notifications"
                onClick={() => setNotificationsOpen(false)}
              >
                <LuX />
              </button>
            </header>
            {approvalNotifications.length ? (
              approvalNotifications.map((notification) => (
                <button
                  type="button"
                  key={notification._id}
                  className={notification.read ? "" : "is-unread"}
                  onClick={() => {
                    socket?.emit("read", {
                      businessUserID: loginDetails.businessUserID,
                      notificationID: notification._id,
                      read: true,
                    });
                    setApprovalNotifications((previous) =>
                      previous.map((entry) =>
                        entry._id === notification._id
                          ? { ...entry, read: true }
                          : entry
                      )
                    );
                    setNotificationsOpen(false);
                    navigate(`/quotations/view/${notification.referenceID}`);
                  }}
                >
                  <span>{notification.message}</span>
                  <small>
                    {new Date(notification.createdDate).toLocaleString("en-AU")}
                  </small>
                </button>
              ))
            ) : (
              <p>No quotation notifications yet.</p>
            )}
          </section>
        )}

        {/* MAIN WORKSPACE CONTENT */}
        <main className="dashboard-content quotation-content">
          {/* TABLET ONLY IDENTITY & ORG ROW (Figma Node 539:64178) */}
          <div className="quotation-tablet-identity-row dashboard-org-product-row">
            <div className="dashboard-product-identity">
              <button
                type="button"
                className="dashboard-launcher-btn"
                onClick={() => setNavOpen(true)}
                aria-label="Open navigation"
              >
                <LuMenu />
              </button>
              <span className="dashboard-product-name">
                invoice &amp; recover
              </span>
            </div>
            <button className="dashboard-mobile-org" type="button">
              <span>{initials(organisation)}</span>
              <div>
                <strong>{organisation}</strong>
                <small>{role}</small>
              </div>
              <LuChevronsUpDown className="org-switch-icon" />
            </button>
          </div>

          {/* MOBILE ONLY IDENTITY & ORG ROW (Figma Node 551:41126) */}
          <div className="quotation-mobile-identity">
            <span className="quotation-mobile-brand-title">
              invoice &amp; recover
            </span>
            <button className="dashboard-mobile-org" type="button">
              <span>{initials(organisation)}</span>
              <div>
                <strong>{organisation}</strong>
                <small>{role}</small>
              </div>
              <LuChevronsUpDown className="org-switch-icon" />
            </button>
          </div>

          {/* PAGE HEADER */}
          <section className="quotation-page-header">
            <div className="quotation-title-row">
              <div className="quotation-page-identity">
                <h1>Quotations</h1>
                <p>Create, send, and track customer quotes.</p>
              </div>
              <button
                className="quotation-create-btn"
                onClick={addQuotation}
                type="button"
              >
                <LuPlus />
                <span>New quote</span>
              </button>
            </div>
            <div className="quotation-snapshot-context">
              <span>
                {organisation}
                {totalRows === 0 && !loading
                  ? " · 0 quotes · All amounts in AUD"
                  : " · All amounts in AUD"}
              </span>
              <span>
                {loading
                  ? "Retrieving the quotation list…"
                  : documentDateFilterLabel(dateFilter)}
              </span>
            </div>
          </section>

          {/* SUMMARY STAT CARDS */}
          <section className="quotation-summary-grid" aria-busy={loading}>
            {loading ? (
              <CardSkeleton variant="stat" count={3} />
            ) : (
              <>
                <article className="quotation-stat-card">
                  <div className="quotation-stat-card-header">
                    <h3>Draft quotes</h3>
                    <LuFileText />
                  </div>
                  <div className="quotation-stat-card-headline">
                    <strong>
                      {loading ? (
                        <Skeleton width={92} height="1.2em" />
                      ) : (
                        formatStatAmount(sum(["Draft"]))
                      )}
                    </strong>
                    <p>
                      {loading ? (
                        <Skeleton width="75%" height="1.4em" />
                      ) : (
                        <>{counts.Draft || 0} quotes · Unsent value</>
                      )}
                    </p>
                  </div>
                </article>

                <article className="quotation-stat-card">
                  <div className="quotation-stat-card-header">
                    <h3>Awaiting decision</h3>
                    <LuFileText />
                  </div>
                  <div className="quotation-stat-card-headline">
                    <strong>
                      {loading ? (
                        <Skeleton width={92} height="1.2em" />
                      ) : (
                        formatStatAmount(
                          sum(["Sent", "Viewed", "Awaiting decision"])
                        )
                      )}
                    </strong>
                    <p>
                      {loading ? (
                        <Skeleton width="75%" height="1.4em" />
                      ) : (
                        <>
                          {awaitingDecisionCount} quotes · Sent / viewed value
                        </>
                      )}
                    </p>
                  </div>
                </article>

                <article className="quotation-stat-card quotation-stat-card-full">
                  <div className="quotation-stat-card-header">
                    <h3>Approved to invoice</h3>
                    <LuFileText />
                  </div>
                  <div className="quotation-stat-card-headline">
                    <strong>
                      {loading ? (
                        <Skeleton width={92} height="1.2em" />
                      ) : (
                        formatStatAmount(
                          statusSummary?.approvedToInvoice?.amount
                        )
                      )}
                    </strong>
                    <p>
                      {loading ? (
                        <Skeleton width="75%" height="1.4em" />
                      ) : (
                        <>
                          {statusSummary?.approvedToInvoice?.count || 0} quote
                          {statusSummary?.approvedToInvoice?.count === 1
                            ? ""
                            : "s"}{" "}
                          · Approved, not converted
                        </>
                      )}
                    </p>
                  </div>
                </article>
              </>
            )}
          </section>

          {/* QUOTATIONS MANAGEMENT */}
          <section className="quotation-management">
            {/* STATUS TABS */}
            <div className="quotation-tabs-wrap">
              <nav className="quotation-tabs" aria-label="Quotation status">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={`quotation-tab-btn${
                      activeTab === tab ? " active" : ""
                    }`}
                    onClick={() => {
                      setActiveTab(tab);
                      setPage(1);
                    }}
                  >
                    {tab}
                    {statusSummary ? ` (${countFor(tab)})` : ""}
                  </button>
                ))}
              </nav>
            </div>

            {/* FILTER CONTROLS */}
            <div className="quotation-filter-controls">
              <div className="quotation-filter-search">
                <LuSearch />
                <ClearableSearchInput
                  id="quotation-search-input"
                  placeholder="Search quote number or customer"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              <div className="quotation-dropdowns-group">
                <SelectDropdown
                  value={customer}
                  onChange={(val) => {
                    setCustomer(val);
                    setPage(1);
                  }}
                  options={customerOptions}
                  className="quotation-customer-filter"
                  ariaLabel="Filter by customer"
                />

                <SelectDropdown
                  value={dateFilter}
                  onChange={(val) => {
                    setDateFilter(val);
                    setPage(1);
                  }}
                  options={dateOptions}
                  ariaLabel="Filter by date range"
                />

                {showClearFilters && (
                  <button
                    type="button"
                    className="quotation-clear-filters-action"
                    onClick={clearFilters}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>

            {/* DESKTOP TABLE (Figma Node 534:47752) */}
            <div className="quotation-table-card quotation-desktop-table-card">
              <div className="quotation-table-context">
                <strong>{totalRows} quotations</strong>
                <span>Potential business · Not receivables</span>
              </div>

              <div className="quotation-table-scroll">
                <table>
                  <colgroup>
                    <col className="col-quoteno" />
                    <col className="col-customer" />
                    <col className="col-created" />
                    <col className="col-valid" />
                    <col className="col-status" />
                    <col className="col-amount" />
                    <col className="col-actions" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="col-quoteno">Quote number</th>
                      <th className="col-customer">Customer</th>
                      <th className="col-created">Created date</th>
                      <th className="col-valid">Valid until</th>
                      <th className="col-status">Status</th>
                      <th className="col-amount">Amount</th>
                      <th className="col-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <TableSkeleton
                        variant="rows"
                        columns={6}
                        rows={skeletonRows}
                        columnWidths={[
                          "75%",
                          "70%",
                          "70%",
                          "70%",
                          "65%",
                          "80%",
                        ]}
                      />
                    ) : visibleRows.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="quotation-state-cell">
                          {renderStateMessage()}
                        </td>
                      </tr>
                    ) : (
                      visibleRows.map((row) => (
                        <tr
                          key={row._id}
                          className="quotation-clickable-row"
                          onClick={(event) => openQuoteDetails(row, event)}
                          onKeyDown={(event) =>
                            handleQuoteRowKeyDown(row, event)
                          }
                          tabIndex={0}
                        >
                          <td className="col-quoteno">
                            <Link
                              to={getQuoteOpenPath(row)}
                              state={getQuoteOpenState(row)}
                              className="quotation-mono-link"
                            >
                              {row.quotationNumber || "—"}
                            </Link>
                          </td>
                          <td className="col-customer">
                            {getContactOpenPath(row) ? (
                              <Link
                                to={getContactOpenPath(row)}
                                className="quotation-contact-link"
                              >
                                {row.customerCompanyName ||
                                  row.customerName ||
                                  "—"}
                              </Link>
                            ) : (
                              row.customerCompanyName || row.customerName || "—"
                            )}
                          </td>
                          <td className="col-created">
                            {formatCreatedDate(row)}
                          </td>
                          <td className="col-valid">
                            {formatValidUntilDesktop(row)}
                          </td>
                          <td className="col-status">
                            <span
                              className={`quotation-status ${statusKey(row)}`}
                            >
                              {rowStatus(row)}
                            </span>
                          </td>
                          <td
                            className="col-amount"
                            title={`$${formatCurrencyAUD(
                              numberValue(row.finalAmount)
                            )}`}
                          >
                            ${formatCurrencyAUD(numberValue(row.finalAmount))}
                          </td>
                          <td className="col-actions quotation-row-action">
                            <div className="quotation-row-actions-group">
                              <button
                                type="button"
                                className="quotation-dots-btn listing-action-trigger"
                                onClick={(event) => {
                                  const isOpen = openMenu === row._id;
                                  setOpenMenu(isOpen ? null : row._id);
                                  setMenuAnchorEl(
                                    isOpen ? null : event.currentTarget
                                  );
                                }}
                                aria-label={`Actions for ${row.quotationNumber}`}
                                aria-expanded={openMenu === row._id}
                              >
                                <LuEllipsis />
                              </button>
                              {openMenu === row._id && renderActionMenu(row)}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <footer className="quotation-pagination">
                <span>{paginationSummaryText}</span>
                <div className="quotation-pagination-btns">
                  <button
                    type="button"
                    disabled={loading || page <= 1 || visibleRows.length === 0}
                    onClick={() => setPage((v) => Math.max(1, v - 1))}
                    aria-label="Previous page"
                  >
                    <LuChevronLeft />
                  </button>
                  <button
                    type="button"
                    disabled={
                      loading || page >= totalPages || visibleRows.length === 0
                    }
                    onClick={() => setPage((v) => Math.min(totalPages, v + 1))}
                    aria-label="Next page"
                  >
                    <LuChevronRight />
                  </button>
                </div>
              </footer>
            </div>

            {/* TABLET TABLE (Figma Node 539:64178) */}
            <div className="quotation-table-card quotation-tablet-table-card">
              <div className="quotation-table-context">
                <strong>{totalRows} quotations</strong>
                <span>Potential business · Not receivables</span>
              </div>

              <div className="quotation-table-scroll">
                <table>
                  <colgroup>
                    <col className="tab-col-custquote" />
                    <col className="tab-col-statusvalid" />
                    <col className="tab-col-amount" />
                    <col className="tab-col-actions" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="tab-col-custquote">Customer / Quote</th>
                      <th className="tab-col-statusvalid">Status / Validity</th>
                      <th className="tab-col-amount">Amount</th>
                      <th className="tab-col-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <TableSkeleton
                        variant="rows"
                        columns={3}
                        rows={skeletonRows}
                        columnWidths={["75%", "70%", "80%"]}
                      />
                    ) : visibleRows.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="quotation-state-cell">
                          {renderStateMessage()}
                        </td>
                      </tr>
                    ) : (
                      visibleRows.map((row) => (
                        <tr
                          key={row._id}
                          className="quotation-clickable-row"
                          onClick={(event) => openQuoteDetails(row, event)}
                          onKeyDown={(event) =>
                            handleQuoteRowKeyDown(row, event)
                          }
                          tabIndex={0}
                        >
                          <td className="tab-col-custquote">
                            <div className="tab-custquote-inner">
                              {getContactOpenPath(row) ? (
                                <Link
                                  to={getContactOpenPath(row)}
                                  className="tab-cust-name quotation-contact-link"
                                >
                                  {row.customerCompanyName ||
                                    row.customerName ||
                                    "—"}
                                </Link>
                              ) : (
                                <span className="tab-cust-name">
                                  {row.customerCompanyName ||
                                    row.customerName ||
                                    "—"}
                                </span>
                              )}
                              <Link
                                to={getQuoteOpenPath(row)}
                                state={getQuoteOpenState(row)}
                                className="quotation-mono-link"
                              >
                                {row.quotationNumber || "—"}
                              </Link>
                              <span className="tab-created-date">
                                Created {formatCreatedDate(row)}
                              </span>
                            </div>
                          </td>
                          <td className="tab-col-statusvalid">
                            <div className="tab-statusvalid-inner">
                              <span
                                className={`quotation-status ${statusKey(row)}`}
                              >
                                {rowStatus(row)}
                              </span>
                              <span className="tab-valid-text">
                                {formatValidUntilTabletMobile(row)}
                              </span>
                            </div>
                          </td>
                          <td className="tab-col-amount">
                            ${formatCurrencyAUD(numberValue(row.finalAmount))}
                          </td>
                          <td className="tab-col-actions quotation-row-action">
                            <div className="quotation-row-actions-group">
                              <button
                                type="button"
                                className="quotation-dots-btn listing-action-trigger"
                                onClick={(event) => {
                                  const isOpen = openMenu === row._id;
                                  setOpenMenu(isOpen ? null : row._id);
                                  setMenuAnchorEl(
                                    isOpen ? null : event.currentTarget
                                  );
                                }}
                                aria-label={`Actions for ${row.quotationNumber}`}
                                aria-expanded={openMenu === row._id}
                              >
                                <LuEllipsis />
                              </button>
                              {openMenu === row._id && renderActionMenu(row)}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <footer className="quotation-pagination">
                <span>{paginationSummaryText}</span>
                <div className="quotation-pagination-btns">
                  <button
                    type="button"
                    disabled={loading || page <= 1 || visibleRows.length === 0}
                    onClick={() => setPage((v) => Math.max(1, v - 1))}
                    aria-label="Previous page"
                  >
                    <LuChevronLeft />
                  </button>
                  <button
                    type="button"
                    disabled={
                      loading || page >= totalPages || visibleRows.length === 0
                    }
                    onClick={() => setPage((v) => Math.min(totalPages, v + 1))}
                    aria-label="Next page"
                  >
                    <LuChevronRight />
                  </button>
                </div>
              </footer>
            </div>

            {/* MOBILE LIST VIEW (Figma Node 551:41126) */}
            <div className="quotation-mobile-view">
              <div className="quotation-mobile-list-header">
                <div className="quotation-mobile-list-heading-left">
                  <strong>{totalRows} quotations</strong>
                  <span>Potential business · Not receivables</span>
                </div>
              </div>

              <div className="quotation-mobile-cards-list">
                {loading ? (
                  Array.from({ length: skeletonRows }, (_, index) => (
                    <article
                      key={index}
                      className="quotation-mobile-card"
                      aria-hidden="true"
                    >
                      <div className="quotation-mobile-card-top">
                        <span className="quotation-mono-link">
                          <Skeleton width={100} height={18} />
                        </span>
                        <Skeleton variant="pill" width={60} height={24} />
                        <div className="quotation-mobile-dots">
                          <Skeleton variant="circular" width={32} height={32} />
                        </div>
                      </div>
                      <div className="quotation-mobile-card-client">
                        <strong>
                          <Skeleton width={140} height="1.55em" />
                        </strong>
                      </div>
                      <div className="quotation-mobile-card-mid">
                        <span className="quotation-mobile-validity">
                          <Skeleton width={130} height="1.4em" />
                        </span>
                        <strong className="quotation-mobile-amount">
                          <Skeleton width={80} height="1.3em" />
                        </strong>
                      </div>
                      <div className="quotation-mobile-card-footer">
                        <Skeleton width={120} height="1.4em" />
                      </div>
                    </article>
                  ))
                ) : visibleRows.length === 0 ? (
                  <div className="quotation-state-cell quotation-state-mobile">
                    {renderStateMessage()}
                  </div>
                ) : (
                  visibleRows.map((row) => (
                    <article
                      key={row._id}
                      className="quotation-mobile-card quotation-clickable-row"
                      onClick={(event) => openQuoteDetails(row, event)}
                      onKeyDown={(event) => handleQuoteRowKeyDown(row, event)}
                      tabIndex={0}
                    >
                      <div className="quotation-mobile-card-top">
                        <Link
                          to={getQuoteOpenPath(row)}
                          state={getQuoteOpenState(row)}
                          className="quotation-mono-link"
                        >
                          {row.quotationNumber || "—"}
                        </Link>
                        <span className={`quotation-status ${statusKey(row)}`}>
                          {rowStatus(row)}
                        </span>
                        <div className="quotation-mobile-dots quotation-row-action">
                          <button
                            type="button"
                            className="quotation-dots-btn listing-action-trigger"
                            onClick={(event) => {
                              const isOpen = openMenu === row._id;
                              setOpenMenu(isOpen ? null : row._id);
                              setMenuAnchorEl(
                                isOpen ? null : event.currentTarget
                              );
                            }}
                            aria-label={`Actions for ${row.quotationNumber}`}
                            aria-expanded={openMenu === row._id}
                          >
                            <LuEllipsis />
                          </button>
                          {openMenu === row._id && renderActionMenu(row)}
                        </div>
                      </div>

                      <div className="quotation-mobile-card-client">
                        <strong>
                          {getContactOpenPath(row) ? (
                            <Link
                              to={getContactOpenPath(row)}
                              className="quotation-contact-link"
                            >
                              {row.customerCompanyName ||
                                row.customerName ||
                                "—"}
                            </Link>
                          ) : (
                            row.customerCompanyName || row.customerName || "—"
                          )}
                        </strong>
                      </div>

                      <div className="quotation-mobile-card-mid">
                        <span className="quotation-mobile-validity">
                          {formatValidUntilTabletMobile(row)}
                        </span>
                        <strong className="quotation-mobile-amount">
                          ${formatCurrencyAUD(numberValue(row.finalAmount))}
                        </strong>
                      </div>

                      <div className="quotation-mobile-card-footer">
                        <span>Created {formatCreatedDate(row)}</span>
                      </div>
                    </article>
                  ))
                )}
              </div>

              <footer className="quotation-pagination quotation-mobile-pagination">
                <span>{paginationSummaryText}</span>
                <div className="quotation-pagination-btns">
                  <button
                    type="button"
                    disabled={loading || page <= 1 || visibleRows.length === 0}
                    onClick={() => setPage((v) => Math.max(1, v - 1))}
                    aria-label="Previous page"
                  >
                    <LuChevronLeft />
                  </button>
                  <button
                    type="button"
                    disabled={
                      loading || page >= totalPages || visibleRows.length === 0
                    }
                    onClick={() => setPage((v) => Math.min(totalPages, v + 1))}
                    aria-label="Next page"
                  >
                    <LuChevronRight />
                  </button>
                </div>
              </footer>
            </div>

            {/* DISCLAIMER NOTE */}
            <p className="quotation-disclaimer">
              Quote amounts represent potential business, not money owed or
              collected. Only approved quotes can be converted to invoices.
            </p>
          </section>
        </main>
      </div>

      <DeleteConfirmationDialog
        open={deleteDialog}
        variant="editor"
        entityName="quote"
        title="Delete quote"
        message={
          <>
            <strong>{deleteNumber || "This quote"}</strong> will remain
            available in the Voided section. This action cannot be undone.
          </>
        }
        confirmLabel="Delete quote"
        confirmTone="primary"
        loadingLabel="Deleting quote…"
        loading={isDeleting}
        error={deleteError}
        handleDelete={deleteQuotation}
        handleCancel={() => setDeleteDialog(false)}
        closeModalClick={() => setDeleteDialog(false)}
      />
      <LoadingModal loading={actionLoading} />

      {/* SEND QUOTE EMAIL MODAL DIALOG (Figma Node 574:39806) */}
      {sendModalOpen && sendQuoteRow && (
        <div
          className="quotation-dialog-scrim"
          data-clickable="true"
          onClick={(e) => {
            if (e.target === e.currentTarget && !sendingEmail && !actionLoading)
              setSendModalOpen(false);
          }}
        >
          <div
            className="quotation-send-dialog"
            role="dialog"
            aria-labelledby="quotation-send-title"
            aria-modal="true"
          >
            {/* Dialog Header */}
            <div className="quotation-dialog-header">
              <h3 id="quotation-send-title">
                {sendAction === "followUp" ? "Follow up quote" : "Send quote"}{" "}
                {sendQuoteRow.quotationNumber || ""}
              </h3>
              <button
                type="button"
                className="quotation-dialog-close-btn"
                onClick={() => setSendModalOpen(false)}
                disabled={sendingEmail || actionLoading}
                aria-label="Close dialog"
              >
                <LuX />
              </button>
            </div>

            {/* Dialog Body */}
            <div className="quotation-dialog-body">
              {/* Recipient */}
              <QuotationRecipientField
                id="send-recipient-input"
                primaryEmail={sendRecipient}
                loading={sendRecipientLoading}
                disabled={sendingEmail || actionLoading}
                primaryError={sendRecipientError}
                additionalError={additionalRecipientsError}
                onChange={(emails) => {
                  setAdditionalRecipients(emails);
                  setSendError("");
                }}
                onError={setAdditionalRecipientsError}
              />

              {/* Subject */}
              <div className="quotation-dialog-field">
                <label htmlFor="send-subject-input">Subject</label>
                <input
                  id="send-subject-input"
                  type="text"
                  value={sendSubject}
                  disabled={sendingEmail || actionLoading}
                  onChange={(e) => setSendSubject(e.target.value)}
                />
              </div>

              {/* Email Preview */}
              <div className="quotation-dialog-preview-section">
                <div className="quotation-dialog-preview-heading">
                  <LuMail />
                  <span>
                    {sendAction === "followUp"
                      ? "Email message"
                      : "Email preview"}
                  </span>
                </div>

                <div className="quotation-dialog-email-card">
                  {sendAction === "followUp" ? (
                    <>
                      <p className="quotation-email-greeting">
                        Hello{" "}
                        {sendQuoteRow.customerCompanyName ||
                          sendQuoteRow.customerName ||
                          "Customer"}
                        ,
                      </p>
                      <textarea
                        id="send-message-input"
                        className="quotation-followup-message"
                        aria-label="Follow-up email message"
                        rows={7}
                        value={sendMessage}
                        disabled={sendingEmail || actionLoading}
                        onChange={(event) => {
                          setSendMessage(event.target.value);
                          setSendError("");
                        }}
                      />
                      <p className="quotation-email-validity">
                        Valid for{" "}
                        {String(sendQuoteRow.validUpto || "14 days").replace(
                          /\bDays?\b/i,
                          "days"
                        )}{" "}
                        from sending.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="quotation-email-greeting">
                        Hello{" "}
                        {sendQuoteRow.customerCompanyName ||
                          sendQuoteRow.customerName ||
                          "Customer"}
                        ,
                      </p>
                      <p className="quotation-email-text">
                        Please find quote {sendQuoteRow.quotationNumber} for $
                        {formatCurrencyAUD(
                          numberValue(sendQuoteRow.finalAmount)
                        )}{" "}
                        AUD from {organisation}. You can review the details and
                        approve your quote online.
                      </p>
                      <p className="quotation-email-validity">
                        Valid for{" "}
                        {String(sendQuoteRow.validUpto || "14 days").replace(
                          /\bDays?\b/i,
                          "days"
                        )}{" "}
                        from sending.
                      </p>
                    </>
                  )}
                  <span className="quotation-email-preview-cta">
                    Review & approve
                  </span>
                  <div className="quotation-email-signoff">
                    <p>Thanks,</p>
                    <p>{organisation}</p>
                  </div>
                </div>
              </div>

              {/* Delivery options */}
              {sendError && (
                <p className="quotation-field-error" role="alert">
                  {sendError}
                </p>
              )}
              <div className="quotation-dialog-delivery-options">
                <label className="quotation-dialog-checkbox">
                  <input
                    type="checkbox"
                    checked={attachPdf}
                    disabled={sendingEmail || actionLoading}
                    onChange={(e) => setAttachPdf(e.target.checked)}
                  />
                  <span>Attach quote PDF</span>
                </label>
                <label className="quotation-dialog-checkbox">
                  <input
                    type="checkbox"
                    checked={sendCopy}
                    disabled={sendingEmail || actionLoading}
                    onChange={(e) => setSendCopy(e.target.checked)}
                  />
                  <span>Send me a copy</span>
                </label>
              </div>
            </div>

            {/* Dialog Footer */}
            <div className="quotation-dialog-footer">
              <button
                type="button"
                className="quotation-dialog-cancel-btn"
                onClick={() => setSendModalOpen(false)}
                disabled={sendingEmail || actionLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="quotation-dialog-send-btn"
                onClick={handleSendQuoteSubmit}
                disabled={
                  sendingEmail ||
                  actionLoading ||
                  sendRecipientLoading ||
                  !sendRecipient
                }
              >
                <LuSend />
                <span>
                  {sendingEmail
                    ? "Sending…"
                    : sendAction === "followUp"
                    ? "Send follow up"
                    : "Send quote"}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {approvalQuote && (
        <div
          className="quotation-dialog-scrim"
          data-clickable="true"
          onClick={() => { if (!approvalLoading) setApprovalQuote(null); }}
        >
          <div
            className="quotation-modal-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="quotation-approval-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="quotation-modal-header">
              <h3 id="quotation-approval-title">Mark quote as approved?</h3>
              <button
                type="button"
                className="quotation-dialog-close-btn"
                aria-label="Close"
                disabled={approvalLoading}
                onClick={() => setApprovalQuote(null)}
              >
                <LuX />
              </button>
            </div>
            <div className="quotation-modal-body">
              <p>
                This records manual approval for{" "}
                <strong>{approvalQuote.quotationNumber}</strong>. Only do this
                after the customer has approved outside the online quote.
              </p>
            </div>
            <div className="quotation-modal-footer">
              <button
                type="button"
                className="quotation-modal-cancel-btn"
                disabled={approvalLoading}
                onClick={() => setApprovalQuote(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="quotation-modal-submit-btn"
                disabled={approvalLoading}
                aria-busy={approvalLoading}
                onClick={() => handleMarkAsApproved(approvalQuote)}
              >
                {approvalLoading ? (
                  <CircularProgress size={16} color="inherit" aria-hidden="true" />
                ) : (
                  <LuCheckCheck />
                )}
                {approvalLoading ? "Approving…" : "Mark as approved"}
              </button>
            </div>
          </div>
        </div>
      )}

      <DocumentSentSuccessDialog
        open={Boolean(sentQuote)}
        documentType="quote"
        documentNumber={sentQuote?.number}
        recipient={sentQuote?.recipient}
        title={sentQuote?.followUp ? "Follow-up sent" : undefined}
        successMessage={sentQuote?.followUp ? "Follow-up sent successfully" : undefined}
        onClose={() => setSentQuote(null)}
        onViewDetails={() => navigate(`/quotations/view/${sentQuote?.id}`)}
      />
    </div>
  );
}

export default memo(Quotations);
